import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { GameId, Seat } from '@sf72/shared';

/**
 * What every board component receives.
 *
 * `state` is already masked for this player by the server, and `legalMoves` is
 * whatever the engine chose to expose. A board renders these and calls
 * `onMove`; it never decides whether a move is allowed.
 */
export interface BoardProps<S = unknown, M = unknown> {
  state: S;
  yourSeat: Seat | null;
  turnSeat: Seat | null;
  legalMoves: M[] | null;
  /** False while a move is in flight, when it is not your turn, or once over. */
  canMove: boolean;
  onMove: (move: M) => void;
  /** Display names indexed by seat. */
  names: string[];
}

/**
 * The erased shape the app renders through.
 *
 * A board is written against its own state and move types, but the app only
 * knows the game id and an opaque `state` from the server. That mismatch is a
 * real trust boundary rather than a typing accident: the pairing of game id to
 * state shape is guaranteed by the engine on the server, and the cast below is
 * the single place that relies on it.
 */
export type BoardComponent = ComponentType<BoardProps>;

function erase<S, M>(component: ComponentType<BoardProps<S, M>>): BoardComponent {
  return component as unknown as BoardComponent;
}

/**
 * Boards are loaded on demand: with thirty games ahead, bundling every board
 * into the entry chunk would make the first paint slower for everyone in order
 * to serve the one game they actually opened.
 */
const BOARDS: Partial<Record<GameId, LazyExoticComponent<BoardComponent>>> = {
  tictactoe: lazy(() => import('./tictactoe/Board.js').then((m) => ({ default: erase(m.Board) }))),
  chess: lazy(() => import('./chess/Board.js').then((m) => ({ default: erase(m.Board) }))),
  battleship: lazy(() =>
    import('./battleship/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  checkers: lazy(() =>
    import('./checkers/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  reversi: lazy(() =>
    import('./reversi/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  blackjack: lazy(() =>
    import('./blackjack/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  backgammon: lazy(() =>
    import('./backgammon/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  durak: lazy(() =>
    import('./durak/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  uno: lazy(() =>
    import('./uno/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  sudoku: lazy(() =>
    import('./sudoku/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  quizduel: lazy(() =>
    import('./quizduel/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  domino: lazy(() =>
    import('./domino/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  dotsboxes: lazy(() =>
    import('./dotsboxes/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  balda: lazy(() =>
    import('./balda/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  anagram: lazy(() =>
    import('./anagram/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  memory: lazy(() =>
    import('./memory/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  mathduel: lazy(() =>
    import('./mathduel/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
  reaction: lazy(() =>
    import('./reaction/Board.js').then((m) => ({ default: erase(m.Board) })),
  ),
};

export function boardFor(gameId: GameId): LazyExoticComponent<BoardComponent> | null {
  return BOARDS[gameId] ?? null;
}
